import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarDays, ShoppingBag, Cross, Map, Heart, Route, Newspaper, Users, Siren, PhoneCall, Download, Shield, Globe, LogIn, LogOut, UserCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSections } from '@/contexts/SectionsContext';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/SEO';
import { toast } from 'sonner';
import { ShareButton } from '@/components/ShareButton';

const T = {
  es: { title: 'Más', subtitle: 'Todo lo que puedes hacer en la guía', events: 'Eventos', booking: 'Reservar', pharmacies: 'Farmacias de guardia', map: 'Mapa', favorites: 'Favoritos', itineraries: 'Itinerarios', blog: 'Blog', about: 'Sobre nosotros', emergencies: 'Emergencias', sos: 'Llamar al 112', settings: 'Ajustes', language: 'Idioma', install: 'Instalar la app', installed: 'App instalada', account: 'Cuenta', login: 'Iniciar sesión', logout: 'Cerrar sesión', admin: 'Panel de administración' },
  en: { title: 'More', subtitle: 'Everything you can do in the guide', events: 'Events', booking: 'Book', pharmacies: 'Duty pharmacies', map: 'Map', favorites: 'Favourites', itineraries: 'Itineraries', blog: 'Blog', about: 'About us', emergencies: 'Emergencies', sos: 'Call 112', settings: 'Settings', language: 'Language', install: 'Install the app', installed: 'App installed', account: 'Account', login: 'Sign in', logout: 'Sign out', admin: 'Admin panel' },
  de: { title: 'Mehr', subtitle: 'Alles, was du im Guide tun kannst', events: 'Events', booking: 'Buchen', pharmacies: 'Notdienst-Apotheken', map: 'Karte', favorites: 'Favoriten', itineraries: 'Reiserouten', blog: 'Blog', about: 'Über uns', emergencies: 'Notfälle', sos: '112 anrufen', settings: 'Einstellungen', language: 'Sprache', install: 'App installieren', installed: 'App installiert', account: 'Konto', login: 'Anmelden', logout: 'Abmelden', admin: 'Admin-Bereich' },
};

const Tile = ({ to, href, icon: Icon, label, color, badge, testId, onClick }) => {
  const inner = (
    <>
      <div className={`relative w-16 h-16 rounded-2xl flex items-center justify-center ${color}`}>
        <Icon size={30} strokeWidth={2} />
        {badge && <span className="absolute -top-1.5 -right-1.5 bg-[#E29578] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow" data-testid={`${testId}-badge`}>{badge}</span>}
      </div>
      <span className="text-xs font-semibold text-[#1D1D1F] text-center leading-tight">{label}</span>
    </>
  );
  const cls = 'flex flex-col items-center gap-2 py-3 rounded-2xl active:scale-95 transition-transform';
  if (href) return <a href={href} className={cls} data-testid={testId}>{inner}</a>;
  if (onClick) return <button onClick={onClick} className={cls} data-testid={testId}>{inner}</button>;
  return <Link to={to} className={cls} data-testid={testId}>{inner}</Link>;
};

const MorePage = () => {
  const { language, setLanguage } = useLanguage();
  const { isSectionVisible } = useSections();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const t = T[language] || T.es;
  const [installPrompt, setInstallPrompt] = useState(null);
  const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone;

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const tiles = [
    isSectionVisible('events') && { to: '/events', icon: CalendarDays, label: t.events, color: 'bg-orange-50 text-orange-500', testId: 'more-events' },
    isSectionVisible('booking') && { to: '/booking', icon: ShoppingBag, label: t.booking, color: 'bg-violet-50 text-violet-500', testId: 'more-booking' },
    isSectionVisible('pharmacies') && { to: '/info?view=pharmacies', icon: Cross, label: t.pharmacies, color: 'bg-emerald-50 text-emerald-600', testId: 'more-pharmacies' },
    isSectionVisible('map') && { to: '/map', icon: Map, label: t.map, color: 'bg-sky-50 text-sky-600', testId: 'more-map' },
    { to: '/favorites', icon: Heart, label: t.favorites, color: 'bg-rose-50 text-rose-500', testId: 'more-favorites' },
    isSectionVisible('itineraries') && { to: '/itineraries', icon: Route, label: t.itineraries, color: 'bg-amber-50 text-amber-600', testId: 'more-itineraries' },
    { to: '/info?view=blog', icon: Newspaper, label: t.blog, color: 'bg-indigo-50 text-indigo-500', testId: 'more-blog' },
    { to: '/info?view=about', icon: Users, label: t.about, color: 'bg-teal-50 text-[#005A63]', testId: 'more-about' },
    { to: '/info?view=emergencies', icon: Siren, label: t.emergencies, color: 'bg-red-50 text-red-500', testId: 'more-emergencies' },
    { href: 'tel:112', icon: PhoneCall, label: t.sos, color: 'bg-red-500 text-white', testId: 'more-sos' },
  ].filter(Boolean);

  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-32" data-testid="more-page">
      <SEO title={t.title} description={t.subtitle} path="/more" lang={language} />
      <div className="px-5 pt-5 pb-1">
        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1D1D1F]" data-testid="more-title">{t.title}</h1>
        <p className="text-sm text-gray-500 mt-1" data-testid="more-subtitle">{t.subtitle}</p>
      </div>

      <div className="grid grid-cols-3 gap-2 px-4 mt-4" data-testid="more-grid">
        {tiles.map(tile => <Tile key={tile.testId} {...tile} />)}
      </div>

      <div className="px-5 mt-8">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">{t.settings}</h2>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100">
          <div className="px-4 py-3 flex items-center gap-3">
            <Globe size={20} className="text-[#38B2AC]" />
            <span className="text-sm font-medium flex-1">{t.language}</span>
            <div className="flex gap-1" data-testid="more-language">
              {['es', 'en', 'de'].map(code => (
                <button key={code} onClick={() => setLanguage(code)}
                  className={`min-w-[44px] h-10 rounded-xl text-sm font-bold transition-colors ${language === code ? 'bg-[#38B2AC] text-white' : 'bg-gray-100 text-gray-600'}`}
                  data-testid={`more-lang-${code}`}>{code.toUpperCase()}</button>
              ))}
            </div>
          </div>
          <button onClick={install} disabled={!installPrompt && !isStandalone}
            className="w-full px-4 py-3 flex items-center gap-3 text-left disabled:opacity-60" data-testid="more-install">
            <Download size={20} className="text-[#38B2AC]" />
            <span className="text-sm font-medium flex-1">{isStandalone ? t.installed : t.install}</span>
          </button>
          <div className="px-4 py-3 border-t border-gray-100">
            <ShareButton kind="app" variant="text" testId="more-share-app" />
          </div>
        </div>

        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 mt-6">{t.account}</h2>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100" data-testid="more-account">
          {user ? (
            <>
              <div className="px-4 py-3 flex items-center gap-3">
                <UserCircle size={20} className="text-[#38B2AC]" />
                <span className="text-sm font-medium flex-1 truncate" data-testid="more-account-email">{user.name || user.email}</span>
              </div>
              {user.is_admin && (
                <button onClick={() => navigate('/admin')} className="w-full px-4 py-3 flex items-center gap-3 text-left" data-testid="more-admin">
                  <Shield size={20} className="text-[#E29578]" />
                  <span className="text-sm font-medium flex-1">{t.admin}</span>
                </button>
              )}
              <button onClick={() => { logout(); toast.success(t.logout); }} className="w-full px-4 py-3 flex items-center gap-3 text-left" data-testid="more-logout">
                <LogOut size={20} className="text-gray-500" />
                <span className="text-sm font-medium flex-1">{t.logout}</span>
              </button>
            </>
          ) : (
            <button onClick={() => navigate('/auth')} className="w-full px-4 py-3 flex items-center gap-3 text-left" data-testid="more-login">
              <LogIn size={20} className="text-[#38B2AC]" />
              <span className="text-sm font-medium flex-1">{t.login}</span>
            </button>
          )}
        </div>
        <p className="text-center text-[11px] text-gray-400 mt-6">Calma Guide Tenerife · calmastays.com</p>
      </div>
    </div>
  );
};

export default MorePage;
